import { AUTH_PATHS } from "@/constants/paths";
import { useAuthStore } from "@/features/auth/store";
import { Navigate, useLocation } from "react-router-dom";
import { useUser } from "@/features/auth/store";
import { isAdmin, getRoleDisplayName } from "@/lib/auth-utils";
import { authCookies } from "@/lib/cookies";
import type { UserRole } from "@/types/user";
import { Button } from "@/components/ui/button";
import { ShieldAlert, LogOut } from "lucide-react";
import { useMyPermissions } from "@/features/permissions/hooks/usePermissionsQueries";

function useSessionGate() {
  const { isAuthenticated, isLoading, logout } = useAuthStore();
  const user = useUser();
  const hasAccessToken = Boolean(
    useAuthStore((state) => state.accessToken) ?? authCookies.getAccessToken(),
  );
  const effectiveUser = user ?? authCookies.getUser();
  const hasSession = hasAccessToken || isAuthenticated;
  const isHydratingStaleSession = isLoading && !hasSession;

  return {
    isAuthenticated,
    isLoading,
    logout,
    user: effectiveUser,
    hasSession,
    isHydratingStaleSession,
  };
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  adminOnly?: boolean;
}

interface PermissionRouteProps {
  children: React.ReactNode;
  permission: string;
  redirectTo?: string;
  deniedMessage?: string;
}

export function PermissionRoute({
  children,
  permission,
  redirectTo = AUTH_PATHS.LOGIN,
  deniedMessage = "عذراً، ليس لديك صلاحية الوصول إلى هذه الصفحة.",
}: PermissionRouteProps) {
  const { isLoading, user, hasSession, isHydratingStaleSession } =
    useSessionGate();
  const location = useLocation();

  if (isHydratingStaleSession) return null;

  if (!hasSession) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (!user) {
    if (isLoading && hasSession) {
      return <>{children}</>;
    }
    return null;
  }

  if (user.mustChangePassword) {
    return <Navigate to={AUTH_PATHS.REQUIRED_PASSWORD_CHANGE} replace />;
  }

  return (
    <PermissionGate permission={permission} deniedMessage={deniedMessage}>
      {children}
    </PermissionGate>
  );
}

function PermissionGate({
  children,
  permission,
  deniedMessage,
}: {
  children: React.ReactNode;
  permission: string;
  deniedMessage: string;
}) {
  const { logout } = useSessionGate();
  const { data, isLoading, isError } = useMyPermissions();

  if (isLoading) return null;

  const permissions = new Set(data?.permissions ?? []);
  if (isError || !permissions.has(permission)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] bg-gray-50/50 p-4">
        <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 max-w-md w-full text-center space-y-6">
          <div className="flex justify-center">
            <ShieldAlert className="h-16 w-16 text-destructive" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900">وصول مرفوض</h1>
            <p className="text-gray-500 text-sm leading-relaxed">
              {deniedMessage}
            </p>
          </div>
          <Button
            onClick={() => logout()}
            variant="default"
            className="w-full flex items-center justify-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            <span>تسجيل الخروج</span>
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

interface RoleRouteProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  redirectTo = AUTH_PATHS.LOGIN,
  adminOnly = false,
}: ProtectedRouteProps) {
  const {
    isLoading,
    logout,
    user,
    hasSession,
    isHydratingStaleSession,
  } = useSessionGate();
  const location = useLocation();

  if (isHydratingStaleSession) return null;

  if (!hasSession) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (!user) {
    if (isLoading && hasSession) {
      return <>{children}</>;
    }

    return null;
  }

  if (adminOnly && !isAdmin(user)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50/50 p-4">
        <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 max-w-md w-full text-center space-y-6">
          <div className="flex justify-center">
            <ShieldAlert className="h-16 w-16 text-destructive" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900">وصول مرفوض</h1>
            <p className="text-gray-500 text-sm leading-relaxed">
              عذراً، غير مصرح لك بالوصول إلى لوحة الإدارة. هذا المحتوى متاح
              للمدراء فقط.
            </p>
          </div>
          <Button
            onClick={() => logout()}
            variant="default"
            className="w-full flex items-center justify-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            <span>تسجيل الخروج</span>
          </Button>
        </div>
      </div>
    );
  }

  if (user.mustChangePassword) {
    return <Navigate to={AUTH_PATHS.REQUIRED_PASSWORD_CHANGE} replace />;
  }

  return <>{children}</>;
}

export function RoleRoute({
  children,
  allowedRoles,
  redirectTo = AUTH_PATHS.LOGIN,
}: RoleRouteProps) {
  const {
    isLoading,
    logout,
    user,
    hasSession,
    isHydratingStaleSession,
  } = useSessionGate();
  const location = useLocation();

  if (isHydratingStaleSession) return null;

  if (!hasSession) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (!user) {
    if (isLoading && hasSession) {
      return <>{children}</>;
    }

    return null;
  }

  if (!allowedRoles.includes(user.role)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] bg-gray-50/50 p-4">
        <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 max-w-md w-full text-center space-y-6">
          <div className="flex justify-center">
            <ShieldAlert className="h-16 w-16 text-destructive" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900">وصول مرفوض</h1>
            <p className="text-gray-500 text-sm leading-relaxed">
              عذراً، ليس لديك صلاحية للوصول إلى هذه الصفحة. دورك الحالي:{" "}
              {getRoleDisplayName(user.role)}.
            </p>
          </div>
          <Button
            onClick={() => logout()}
            variant="default"
            className="w-full flex items-center justify-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            <span>تسجيل الخروج</span>
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

interface PublicRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

export function PublicRoute({ children, redirectTo = "/" }: PublicRouteProps) {
  const { isAuthenticated } = useAuthStore();
  const user = useUser();
  const hasAccessToken = Boolean(
    useAuthStore((state) => state.accessToken) ?? authCookies.getAccessToken(),
  );

  const effectiveUser = user ?? authCookies.getUser();

  if (isAuthenticated || hasAccessToken) {
    if (effectiveUser?.mustChangePassword) {
      return <Navigate to={AUTH_PATHS.REQUIRED_PASSWORD_CHANGE} replace />;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
